import { useState, useEffect, useCallback } from 'react';
import { apiErrorText } from '../lib/apiError';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { Smartphone, Fingerprint, Usb, Loader2, Copy, Download, Building2, KeyRound, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import SettingsHeader from './SettingsHeader';
import apiV2 from '../utils/apiV2';

const StatusChip = ({ on, labelOn = 'On', labelOff = 'Off' }) => (
  <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-md border shrink-0 ${
    on ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-600'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${on ? 'bg-emerald-500' : 'bg-gray-400'}`} />
    {on ? labelOn : labelOff}
  </span>
);

const MethodCard = ({ icon: Icon, title, subtitle, on, chip, children, testId }) => (
  <div className="settings-card p-4 sm:p-5" data-testid={testId}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-start gap-3 min-w-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${on ? 'bg-emerald-50' : 'bg-gray-100'}`}>
          <Icon className={`w-5 h-5 ${on ? 'text-emerald-600' : 'text-gray-500'}`} />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {chip !== undefined ? chip : <StatusChip on={on} />}
    </div>
    {children}
  </div>
);

const RecoveryCodesPanel = ({ codes, onDone }) => {
  const copyAll = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    toast.success('Recovery codes copied');
  };
  const download = () => {
    const blob = new Blob([`BookingFlows recovery codes\n\n${codes.join('\n')}\n\nEach code works once. Store them somewhere safe.`], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'bookingflows-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="recovery-codes-panel">
      <p className="text-sm font-semibold text-amber-900">Save your recovery codes</p>
      <p className="text-xs text-amber-700 mt-0.5 mb-3">Each code signs you in once if you lose your authenticator. This is the only time they're shown.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-3" data-testid="recovery-codes-list">
        {codes.map(c => (
          <code key={c} className="text-xs font-mono bg-white border border-amber-200 rounded px-2 py-1 text-center">{c}</code>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={copyAll} className="h-8 text-xs" data-testid="copy-recovery-codes">
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
        </Button>
        <Button variant="outline" size="sm" onClick={download} className="h-8 text-xs" data-testid="download-recovery-codes">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Download
        </Button>
        <Button size="sm" onClick={onDone} className="h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white ml-auto" data-testid="recovery-codes-done">
          I've saved them
        </Button>
      </div>
    </div>
  );
};

export const OperatorSecuritySettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // TOTP
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  // WebAuthn
  const [credentials, setCredentials] = useState([]);
  const [platformSupported, setPlatformSupported] = useState(false);
  const [registering, setRegistering] = useState(false);

  const phoneLabel = status?.org_country && ['UAE', 'AE', 'UNITED ARAB EMIRATES'].includes(String(status.org_country).toUpperCase()) ? 'WhatsApp' : 'SMS';

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiV2.get('/auth/account/2fa/status');
      setStatus(res.data);
    } catch {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCredentials = useCallback(async () => {
    try {
      const res = await apiV2.get('/auth/webauthn/credentials');
      setCredentials(res.data.credentials || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchCredentials();
    if (window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(setPlatformSupported).catch(() => {});
    }
  }, [fetchStatus, fetchCredentials]);

  // ── One-time codes (always email + phone — no email-only option) ──
  const saveOtp = async (enabled) => {
    setBusy(true);
    try {
      const res = await apiV2.post('/auth/account/2fa/otp', { enabled, channel: 'phone' });
      toast.success(enabled ? 'One-time codes enabled' : 'One-time codes disabled');
      setStatus(s => ({ ...s, otp_enabled: res.data.otp_enabled, otp_channel: res.data.otp_channel }));
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to update'));
    } finally {
      setBusy(false);
    }
  };

  // ── Authenticator app ──────────────────────────────────────────
  const startTotp = async () => {
    setBusy(true);
    try {
      const res = await apiV2.post('/auth/account/2fa/totp/setup');
      setTotpSetup(res.data);
      setTotpCode('');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to start setup'));
    } finally {
      setBusy(false);
    }
  };

  const confirmTotp = async () => {
    setBusy(true);
    try {
      const res = await apiV2.post('/auth/account/2fa/totp/confirm', { code: totpCode.trim() });
      setRecoveryCodes(res.data.recovery_codes || []);
      setTotpSetup(null);
      setStatus(s => ({ ...s, totp_enabled: true, recovery_codes_remaining: (res.data.recovery_codes || []).length, recovery_codes_total: (res.data.recovery_codes || []).length }));
      toast.success('Authenticator app enabled');
    } catch (err) {
      toast.error(apiErrorText(err, 'Incorrect code'));
    } finally {
      setBusy(false);
    }
  };

  const disableTotp = async () => {
    if (!window.confirm('Disable the authenticator app? Your recovery codes will also be invalidated.')) return;
    setBusy(true);
    try {
      await apiV2.post('/auth/account/2fa/totp/disable');
      setStatus(s => ({ ...s, totp_enabled: false, recovery_codes_remaining: 0, recovery_codes_total: 0 }));
      setRecoveryCodes(null);
      toast.success('Authenticator app disabled');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to disable'));
    } finally {
      setBusy(false);
    }
  };

  const regenCodes = async () => {
    if (!window.confirm('Generate new recovery codes? All previous codes stop working.')) return;
    setBusy(true);
    try {
      const res = await apiV2.post('/auth/account/2fa/recovery-codes/regenerate');
      setRecoveryCodes(res.data.recovery_codes || []);
      setStatus(s => ({ ...s, recovery_codes_remaining: (res.data.recovery_codes || []).length, recovery_codes_total: (res.data.recovery_codes || []).length }));
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to regenerate'));
    } finally {
      setBusy(false);
    }
  };

  // ── Passkeys & hardware keys ───────────────────────────────────
  const registerKey = async (mode) => {
    setRegistering(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const _ax = (await import('axios')).default;
      const ax = _ax.create({ baseURL: process.env.REACT_APP_BACKEND_URL, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });

      const options = (await ax.post('/api/v2/auth/webauthn/register-options', { origin: window.location.origin })).data;
      const parseB64 = s => { const b = s.replace(/-/g, '+').replace(/_/g, '/'); const bin = atob(b.padEnd(b.length + (4 - b.length % 4) % 4, '=')); return Uint8Array.from(bin, c => c.charCodeAt(0)); };
      const isKey = mode === 'security-key';
      const pko = {
        challenge: parseB64(options.challenge),
        rp: { id: options.rp?.id, name: options.rp?.name },
        user: { id: parseB64(options.user?.id), name: options.user?.name, displayName: options.user?.displayName },
        pubKeyCredParams: (options.pubKeyCredParams || []).map(p => ({ type: p.type, alg: p.alg })),
        timeout: options.timeout || 60000,
        excludeCredentials: (options.excludeCredentials || []).map(c => ({ type: 'public-key', id: parseB64(c.id) })),
        authenticatorSelection: isKey
          ? { authenticatorAttachment: 'cross-platform', residentKey: 'discouraged', userVerification: 'preferred' }
          : { authenticatorAttachment: 'platform', residentKey: 'preferred', userVerification: 'preferred' },
        attestation: 'none',
      };
      const cred = await navigator.credentials.create({ publicKey: pko });
      const toB64 = buf => { const b = new Uint8Array(buf); let s = ''; b.forEach(x => s += String.fromCharCode(x)); return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, ''); };
      const dn = isKey
        ? (window.prompt('Name this security key', 'Security key') || 'Security key').slice(0, 60)
        : (/iPhone/.test(navigator.userAgent) ? 'iPhone' : /iPad/.test(navigator.userAgent) ? 'iPad' : /Mac/.test(navigator.userAgent) ? 'Mac' : /Android/.test(navigator.userAgent) ? 'Android' : 'This device');
      await ax.post('/api/v2/auth/webauthn/register', {
        id: cred.id, rawId: toB64(cred.rawId), type: cred.type,
        response: { attestationObject: toB64(cred.response.attestationObject), clientDataJSON: toB64(cred.response.clientDataJSON) },
        deviceName: dn, kind: isKey ? 'security_key' : 'passkey', origin: window.location.origin,
      });
      toast.success(isKey ? 'Security key registered!' : 'Passkey registered! Sign in with Face ID / Touch ID next time.');
      fetchCredentials();
    } catch (err) {
      if (err.name === 'NotAllowedError') toast.error('Registration cancelled or timed out');
      else if (err.name === 'InvalidStateError') toast.error('This key or device is already registered on your account — try a different one');
      else toast.error(apiErrorText(err, err.message || 'Failed to register'));
    } finally {
      setRegistering(false);
    }
  };

  const removeKey = async (credId) => {
    try {
      await apiV2.delete(`/auth/webauthn/credentials/${credId}`);
      setCredentials(p => p.filter(c => c.id !== credId));
      toast.success('Removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  // ── Org enforcement ────────────────────────────────────────────
  const toggleOrgPolicy = async () => {
    const enabling = !status.org_enforced;
    if (!window.confirm(enabling
      ? 'Require two-factor authentication for EVERYONE in your organization? Members without a method set up will receive one-time codes at sign-in (email + phone).'
      : 'Stop requiring two-factor authentication organization-wide? Members with personal 2FA keep it.')) return;
    setBusy(true);
    try {
      const res = await apiV2.post('/auth/account/2fa/org-policy', { enabled: enabling });
      setStatus(s => ({ ...s, org_enforced: res.data.org_enforced }));
      toast.success(enabling ? '2FA is now required for your whole team' : 'Org-wide 2FA requirement removed');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to update policy'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!status) return null;

  const passkeys = credentials.filter(c => c.kind === 'passkey');
  const securityKeys = credentials.filter(c => c.kind !== 'passkey');
  const anyMethodOn = status.otp_enabled || status.totp_enabled || credentials.length > 0;

  const CredRow = ({ pk }) => (
    <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg gap-2">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{pk.deviceName || 'Device'}</p>
        <p className="text-xs text-gray-400">Added {pk.createdAt ? new Date(pk.createdAt).toLocaleDateString() : ''}</p>
      </div>
      <button onClick={() => removeKey(pk.id)} className="text-xs text-red-500 hover:text-red-700 shrink-0" data-testid={`remove-credential-${pk.id}`}>Remove</button>
    </div>
  );

  return (
    <div className="max-w-2xl space-y-4" data-testid="operator-security-settings">
      <SettingsHeader
        title="Two-Factor Authentication"
        subtitle="Add a second step to your sign-in. Pick any method — or several."
      />

      {status.org_enforced && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800" data-testid="org-enforced-banner">
          Your organization requires two-factor authentication.{!anyMethodOn && ' Sign-in codes go to your email — and your phone once you add a number — until you pick a method below.'}
        </div>
      )}

      {/* 1 — One-time codes (always email + phone — no email-only option) */}
      <MethodCard
        icon={Smartphone}
        title="One-time codes"
        subtitle={`A 6-digit code at every sign-in — sent to both your email and your phone by ${phoneLabel}.`}
        on={status.otp_enabled}
        testId="otp-method-card"
      >
        <div className="space-y-3">
          {!status.phone_on_file && (
            <p className="text-[11px] text-amber-600" data-testid="otp-phone-required-note">
              Add a phone number in My Profile first — codes always go to both your email and your phone ({phoneLabel}), for redundancy.
            </p>
          )}
          {!status.otp_enabled ? (
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white h-9"
              onClick={() => {
                if (!status.phone_on_file) { toast.error('Add a phone number in My Profile first'); return; }
                saveOtp(true);
              }}
              disabled={busy || !status.phone_on_file}
              data-testid="enable-2fa-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Enable email + ${phoneLabel} codes`}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 h-9"
              onClick={() => saveOtp(false)}
              disabled={busy}
              data-testid="disable-2fa-btn"
            >
              Disable one-time codes
            </Button>
          )}
        </div>
      </MethodCard>

      {/* 2 — Authenticator app */}
      <MethodCard
        icon={KeyRound}
        title="Authenticator app"
        subtitle="Codes from Google Authenticator, 1Password, Authy — works offline, no phone signal needed."
        on={status.totp_enabled}
        testId="totp-method-card"
      >
        {!status.totp_enabled && !totpSetup && (
          <Button className="bg-blue-600 hover:bg-blue-700 text-white h-9" onClick={startTotp} disabled={busy} data-testid="totp-setup-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set up authenticator'}
          </Button>
        )}

        {totpSetup && (
          <div className="space-y-3 p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid="totp-setup-panel">
            <p className="text-xs text-gray-600">1. Scan this QR code with your authenticator app:</p>
            <div className="flex justify-center bg-white p-3 rounded-lg border border-gray-200 w-fit mx-auto">
              <QRCodeCanvas value={totpSetup.otpauth_uri} size={170} data-testid="operator-totp-qr" />
            </div>
            <p className="text-[11px] text-gray-500 break-all">Can't scan? Enter this key manually: <code className="font-mono bg-white border border-gray-200 rounded px-1">{totpSetup.manual_key}</code></p>
            <p className="text-xs text-gray-600">2. Enter the 6-digit code the app shows:</p>
            <div className="flex flex-wrap gap-2">
              <Input
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                data-1p-ignore
                data-lpignore="true"
                spellCheck={false}
                className="h-10 w-32 text-center font-mono tracking-[0.3em]"
                data-testid="totp-confirm-input"
              />
              <Button onClick={confirmTotp} disabled={busy || totpCode.length !== 6} className="bg-blue-600 hover:bg-blue-700 text-white h-10" data-testid="totp-confirm-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & enable'}
              </Button>
              <Button variant="ghost" onClick={() => setTotpSetup(null)} className="h-10 text-gray-500" data-testid="totp-cancel-btn">Cancel</Button>
            </div>
          </div>
        )}

        {status.totp_enabled && !recoveryCodes && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Recovery codes: <span className="font-semibold text-gray-700">{status.recovery_codes_remaining} of {status.recovery_codes_total}</span> remaining
            </p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={regenCodes} disabled={busy} className="h-8 text-xs" data-testid="totp-regen-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> New recovery codes
              </Button>
              <Button variant="outline" size="sm" onClick={disableTotp} disabled={busy} className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300" data-testid="totp-disable-btn">
                Disable
              </Button>
            </div>
          </div>
        )}

        {recoveryCodes && <RecoveryCodesPanel codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />}
      </MethodCard>

      {/* 3 — Passkeys */}
      <MethodCard
        icon={Fingerprint}
        title="Passkeys"
        subtitle="Sign in with Face ID, Touch ID or your device PIN — phishing-resistant, nothing to type."
        on={passkeys.length > 0}
        chip={<StatusChip on={passkeys.length > 0} labelOn={`${passkeys.length} active`} />}
        testId="passkeys-method-card"
      >
        <div className="space-y-2">
          {passkeys.map(pk => <CredRow key={pk.id} pk={pk} />)}
          {!platformSupported && passkeys.length === 0 && (
            <p className="text-[11px] text-amber-600">This device has no built-in biometric authenticator — use a hardware security key instead.</p>
          )}
          {platformSupported && (
            <button onClick={() => registerKey('passkey')} disabled={registering} data-testid="operator-add-passkey"
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50">
              {registering ? 'Setting up...' : '+ Add passkey'}
            </button>
          )}
        </div>
      </MethodCard>

      {/* 4 — Hardware security keys */}
      <MethodCard
        icon={Usb}
        title="Hardware security keys"
        subtitle="A physical key (YubiKey, Titan) you plug in on desktop — or tap against your phone (NFC) — the strongest protection available."
        on={securityKeys.length > 0}
        chip={<StatusChip on={securityKeys.length > 0} labelOn={`${securityKeys.length} active`} />}
        testId="security-keys-method-card"
      >
        <div className="space-y-2">
          {securityKeys.map(pk => <CredRow key={pk.id} pk={pk} />)}
          <button onClick={() => registerKey('security-key')} disabled={registering} data-testid="operator-add-security-key"
            className="text-sm bg-white border border-gray-300 hover:bg-gray-50 text-gray-800 px-3 py-1.5 rounded-lg font-medium disabled:opacity-50">
            {registering ? 'Setting up...' : '+ Add security key'}
          </button>
        </div>
      </MethodCard>

      {/* 5 — Org-wide enforcement (owners/admins only) */}
      {status.is_owner && (
        <MethodCard
          icon={Building2}
          title="Require 2FA for your whole team"
          subtitle="Every admin and team member must verify with a second factor at sign-in. Members without a method get one-time codes — by email and their phone."
          on={status.org_enforced}
          chip={<StatusChip on={status.org_enforced} labelOn="Enforced" labelOff="Optional" />}
          testId="org-policy-card"
        >
          <button
            onClick={toggleOrgPolicy}
            disabled={busy}
            role="switch"
            aria-checked={status.org_enforced}
            data-testid="org-2fa-toggle"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${status.org_enforced ? 'bg-blue-600' : 'bg-gray-300'} ${busy ? 'opacity-50' : ''}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${status.org_enforced ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
          <span className="ml-3 text-xs text-gray-500 align-middle">{status.org_enforced ? 'Required for everyone' : 'Each member decides'}</span>
        </MethodCard>
      )}
    </div>
  );
};

export default OperatorSecuritySettings;
